import * as React from 'react';
import { HocOptions } from '../../../../common/generic-hoc.types';
import { StylesProps } from '../../../../../theme/jss-types';
import { RouteChildrenProps } from 'react-router';
import { bindActionCreators } from 'redux';
import { ReduxStore } from '../../../../../reducers/redux.types';
import { Master } from '../../../../../types/master-data-types';
import { Buckets } from '../../../../../library/Types';
import {
    setFilters,
    applyFilters,
} from '../../../../../actions/generic-action';
import GenericHoc from '../../../../common/generic-hoc';
import { laneView1Styles } from './lane-view-1.styles';
import FilterBar from '../../grid_view_1/list/filter-bar';
import GridView1Card from '../../grid_view_1/list/grid-view-1-card';
import { Spin } from 'antd';
import { fetchConsignments } from '../../../../../network/consignments.api';
import { getFilters, buildDeliverySlotTimeFilter } from '../../../consignments.helper';
import { useTranslation } from 'react-i18next';
import { DASHBOARD_STATUS_GROUPS } from '../../grid_view_1/constants';
import { getMoreFilters } from '../../../../../actions/consignment-actions';
import moment from 'moment';

interface LaneView1ListViewProps
    extends StylesProps<ReturnType<typeof laneView1Styles>>, RouteChildrenProps {
    master: Master;
    setfilters: (bucketId: Buckets.CONSIGNMENTS, filters: any) => void;
    applyfilters: (bucketId: Buckets.CONSIGNMENTS) => void;
    filters: Record<string, any>;
    goToDetailsPage: (row: any) => void;
    viewMode: string | null | undefined;
    onViewModeChange: (mode: string | null) => void;
    viewIdFromConfig: string | null | undefined;
    isMoreFilterAdded: () => boolean;
    handleResetFilters: () => void;
}

interface LaneState {
    data: any[];
    loading: boolean;
    loadingMore: boolean;
    currentPage: number;
    hasMore: boolean;
}

type LaneType = 'In Transit' | 'In Process' | 'Delivered' | 'Cancelled';

const LANE_STATUS_FILTERS: Record<LaneType, string> = {
    'In Transit': DASHBOARD_STATUS_GROUPS['In Transit'].join(','),
    'In Process': DASHBOARD_STATUS_GROUPS['In Process'].join(','),
    Delivered: DASHBOARD_STATUS_GROUPS.Delivered.join(','),
    Cancelled: DASHBOARD_STATUS_GROUPS.Cancelled.join(','),
};

const LANE_ORDER: LaneType[] = ['In Transit', 'In Process', 'Delivered', 'Cancelled'];

const LaneView1ListViewComponent: React.FC<LaneView1ListViewProps> = (props) => {
    const {
        master,
        classes,
        filters,
        setfilters,
        applyfilters,
        goToDetailsPage,
        viewMode,
        onViewModeChange,
        viewIdFromConfig,
        isMoreFilterAdded,
        handleResetFilters,
    } = props;

    const { config } = master;
    const { t } = useTranslation();
    const [selectedSearchType, setSelectedSearchType] = React.useState<string>(
        filters?.customerReferenceNumberList ? 'customerReferenceNumberList' : 'referenceNumberList',
    );
    const [inputFilterVal, setInputFilterVal] = React.useState<string>(
        filters?.customerReferenceNumberList ?? filters?.referenceNumberList ?? '',
    );

    // Per-lane state management
    const [lanes, setLanes] = React.useState<Record<LaneType, LaneState>>({
        'In Transit': {
            data: [], loading: false, loadingMore: false, currentPage: 1, hasMore: true,
        },
        'In Process': {
            data: [], loading: false, loadingMore: false, currentPage: 1, hasMore: true,
        },
        Delivered: {
            data: [], loading: false, loadingMore: false, currentPage: 1, hasMore: true,
        },
        Cancelled: {
            data: [], loading: false, loadingMore: false, currentPage: 1, hasMore: true,
        },
    });

    // Refs for infinite scroll
    const laneScrollRefs = React.useRef<Record<LaneType, HTMLDivElement | null>>({
        'In Transit': null,
        'In Process': null,
        Delivered: null,
        Cancelled: null,
    });

    // Track if initial load is done
    const initialLoadRef = React.useRef(false);
    const prevFiltersRef = React.useRef<any>(null);

    // Function to load data for a specific lane
    const loadLaneData = React.useCallback(async (laneType: LaneType, page: number = 1) => {
        const dateFormat = 'YYYY-MM-DD';
        const resultsPerPage = 20;

        // Update loading state
        setLanes((prev) => ({
            ...prev,
            [laneType]: {
                ...prev[laneType],
                loading: page === 1,
                loadingMore: page > 1,
            },
        }));

        try {
            const statusList = LANE_STATUS_FILTERS[laneType];
            const moreFilters = getMoreFilters(filters);

            const filtersInfo = getFilters(
                filters.bucketSelected || 'customer_portal_all',
                config,
            );
            filtersInfo.forEach((filter: any) => {
                if (moreFilters[filter.id] && filter.mode === 'multiple' && typeof moreFilters[filter.id] === 'string') {
                    moreFilters[filter.id] = moreFilters[filter.id].split(',').map((value: string) => value.trim());
                }
            });

            const deliverySlotValue = moreFilters.delivery_time_slot || moreFilters.deliveryTimeslot;
            const filteredMoreFilters = { ...moreFilters };
            // Remove fields that we'll provide explicitly
            delete filteredMoreFilters.delivery_time_slot;
            delete filteredMoreFilters.deliveryTimeslot;
            delete filteredMoreFilters.startDate;
            delete filteredMoreFilters.endDate;
            delete filteredMoreFilters.bucketSelected;
            delete filteredMoreFilters.sortBy;
            delete filteredMoreFilters.descendingOrder;
            delete filteredMoreFilters.dateType;

            const baseTimeFilters = [
                {
                    columnName: filters.dateType || filters.sortBy,
                    fromDateString: moment(filters.startDate).format(dateFormat),
                    toDateString: moment(filters.endDate).format(dateFormat),
                },
            ];
            const deliverySlotTimeFilter = buildDeliverySlotTimeFilter(deliverySlotValue);
            const finalizedTimeFilters = deliverySlotTimeFilter
                ? [...baseTimeFilters, deliverySlotTimeFilter]
                : baseTimeFilters;

            // Ensure customerIdList is always an array
            let customerIdListArray: any[] = [];
            if (Array.isArray(filteredMoreFilters.customerIdList)) {
                customerIdListArray = filteredMoreFilters.customerIdList;
            } else if (filteredMoreFilters.customerIdList) {
                customerIdListArray = [filteredMoreFilters.customerIdList];
            }

            // Prepare reference number list for search
            let referenceNumberList: string[] = [];
            if (filters.referenceNumberList) {
                referenceNumberList = Array.isArray(filters.referenceNumberList)
                    ? filters.referenceNumberList
                    : [filters.referenceNumberList];
            }

            let customerReferenceNumberList: string[] | undefined;
            if (filters.customerReferenceNumberList) {
                customerReferenceNumberList = Array.isArray(filters.customerReferenceNumberList)
                    ? filters.customerReferenceNumberList
                    : [filters.customerReferenceNumberList];
            }

            // Override statusList with lane-specific statuses
            const response = await fetchConsignments({
                ...filteredMoreFilters,
                customerIdList: customerIdListArray,
                bucket: filters.bucketSelected,
                timeFilters: finalizedTimeFilters,
                descendingOrder: filters.descendingOrder,
                referenceNumberList,
                customerReferenceNumberList,
                statusList: statusList.split(','), // Lane-specific status filter
                resultPerPage: resultsPerPage,
                pageNumber: page,
                startDate: moment(filters.startDate).format(dateFormat),
                endDate: moment(filters.endDate).format(dateFormat),
                sortBy: filters.sortBy || filters.dateType,
            });

            if (response.isSuccess) {
                const newData = response.data?.page_data || [];
                const isNextPresent = response.data?.isNextPresent || false;

                // Client-side filter: Only show consignments whose status matches this lane
                const laneStatusList = statusList.split(',').map((s) => s.trim().toLowerCase());
                const filteredData = newData.filter((consignment: any) => {
                    const consignmentStatus = (consignment.status || '').toLowerCase();
                    return laneStatusList.includes(consignmentStatus);
                });

                setLanes((prev) => ({
                    ...prev,
                    [laneType]: {
                        data: page === 1 ? filteredData : [...prev[laneType].data, ...filteredData],
                        loading: false,
                        loadingMore: false,
                        currentPage: page,
                        hasMore: isNextPresent,
                    },
                }));
            } else {
                // Error state
                setLanes((prev) => ({
                    ...prev,
                    [laneType]: {
                        ...prev[laneType],
                        loading: false,
                        loadingMore: false,
                        hasMore: false,
                    },
                }));
            }
        } catch (error) {
            // Error loading lane data
            setLanes((prev) => ({
                ...prev,
                [laneType]: {
                    ...prev[laneType],
                    loading: false,
                    loadingMore: false,
                    hasMore: false,
                },
            }));
        }
    }, [filters, config]);

    // Initial load - fetch all 4 lanes
    React.useEffect(() => {
        if (!initialLoadRef.current) {
            initialLoadRef.current = true;
            LANE_ORDER.forEach((laneType) => {
                loadLaneData(laneType, 1);
            });
        }
    }, []);

    // Detect filter changes and reload all lanes
    React.useEffect(() => {
        if (!initialLoadRef.current) {
            prevFiltersRef.current = filters;
            return;
        }

        const filtersChanged = prevFiltersRef.current === null
            || JSON.stringify(prevFiltersRef.current) !== JSON.stringify(filters);

        if (filtersChanged) {
            prevFiltersRef.current = filters;
            LANE_ORDER.forEach((laneType) => {
                setLanes((prev) => ({
                    ...prev,
                    [laneType]: {
                        data: [],
                        loading: true,
                        loadingMore: false,
                        currentPage: 1,
                        hasMore: true,
                    },
                }));
                loadLaneData(laneType, 1);
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    // Intersection Observer for infinite scroll per lane
    React.useEffect(() => {
        const observers: IntersectionObserver[] = [];

        LANE_ORDER.forEach((laneType) => {
            const handleIntersection = (entries: IntersectionObserverEntry[]) => {
                const firstEntry = entries[0];
                const laneState = lanes[laneType];

                if (firstEntry.isIntersecting
                    && laneState.hasMore
                    && !laneState.loading
                    && !laneState.loadingMore
                ) {
                    loadLaneData(laneType, laneState.currentPage + 1);
                }
            };

            const observer = new IntersectionObserver(handleIntersection, { threshold: 0.1 });
            const ref = laneScrollRefs.current[laneType];

            if (ref) {
                observer.observe(ref);
                observers.push(observer);
            }
        });

        return () => {
            observers.forEach((observer) => observer.disconnect());
        };
    }, [lanes, loadLaneData]);

    // Handle date change
    const handleDateChange = (value: any) => {
        if (!(value && value[0] && value[1])) return;
        const newFilters = {
            ...filters,
        };
        if (value[1].isSame(filters.endDate)) {
            if (moment(filters.endDate).diff(value[0], 'days') > 30) {
                const endDate = moment(value[0]).add(30, 'd');
                newFilters.endDate = endDate.unix() * 1000;
                newFilters.startDate = moment(value[0]).unix() * 1000;
            } else {
                newFilters.endDate = moment(value[1]).unix() * 1000;
                newFilters.startDate = moment(value[0]).unix() * 1000;
            }
        } else if (moment(value[1]).diff(value[0], 'days') > 30) {
            const startDate = moment(value[1]).subtract(30, 'd');
            newFilters.startDate = startDate.unix() * 1000;
            newFilters.endDate = moment(value[1]).unix() * 1000;
        } else {
            newFilters.endDate = moment(value[1]).unix() * 1000;
            newFilters.startDate = moment(value[0]).unix() * 1000;
        }
        setfilters(Buckets.CONSIGNMENTS, newFilters);
        applyfilters(Buckets.CONSIGNMENTS);
    };

    // Handle filter changes
    const onChangeFilter = (filter: string, value: any) => {
        const newFilters = {
            ...filters,
            [filter]: value || undefined,
        };
        setfilters(Buckets.CONSIGNMENTS, newFilters);
        applyfilters(Buckets.CONSIGNMENTS);
    };

    // Handle refresh
    const handleRefresh = () => {
        LANE_ORDER.forEach((laneType) => {
            loadLaneData(laneType, 1);
        });
    };

    // Handle search type change
    const handleSearchTypeChange = (value: string) => {
        setSelectedSearchType(value);
    };

    // Handle search value change
    const handleSearchValueChange = (value: string) => {
        setInputFilterVal(value);
    };

    // Handle search input submit
    const handleSearchInputSubmit = (searchType: string, searchValue: string) => {
        const newFilters = {
            ...filters,
            referenceNumberList: undefined,
            customerReferenceNumberList: undefined,
            [searchType]: searchValue && searchValue.trim() ? searchValue.trim() : undefined,
        };
        setfilters(Buckets.CONSIGNMENTS, newFilters);
        applyfilters(Buckets.CONSIGNMENTS);
    };

    // Render individual lane
    const renderLane = (laneType: LaneType) => {
        const laneState = lanes[laneType];
        const headerClassMap: Record<LaneType, string> = {
            'In Transit': classes.laneHeaderInTransit,
            'In Process': classes.laneHeaderInProcess,
            Delivered: classes.laneHeaderDelivered,
            Cancelled: classes.laneHeaderCancelled,
        };

        return (
            <div key={laneType} className={classes.lane}>
                <div className={`${classes.laneHeader} ${headerClassMap[laneType]}`}>
                    <div className={classes.laneHeaderTitle}>
                        <span>{t(laneType)}</span>
                        <span className={classes.laneCount}>
                            (
                            {laneState.data.length}
                            )
                        </span>
                    </div>
                </div>

                <div className={classes.laneContent}>
                    {/* Initial loading state */}
                    {laneState.loading && laneState.data.length === 0 && (
                        <div className={classes.laneLoader}>
                            <Spin size="large" />
                        </div>
                    )}

                    {/* Empty state */}
                    {!laneState.loading && laneState.data.length === 0 && (
                        <div className={classes.emptyLane}>
                            {t('no_data_found')}
                        </div>
                    )}

                    {/* Render cards */}
                    {laneState.data.map((consignment: any) => (
                        <div key={consignment.reference_number} className={classes.laneCard}>
                            <GridView1Card
                                consignment={consignment}
                                onClick={() => goToDetailsPage(consignment)}
                                classes={classes as any}
                            />
                        </div>
                    ))}

                    {/* Load more trigger */}
                    {laneState.hasMore && (
                        <div
                            ref={(el) => {
                                laneScrollRefs.current[laneType] = el;
                            }}
                            className={classes.loadMoreContainer}
                        >
                            {laneState.loadingMore && <Spin />}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className={classes.mainDiv}>
            <FilterBar
                classes={classes as any}
                filters={filters}
                onDateChange={handleDateChange}
                onChangeFilter={onChangeFilter}
                onRefresh={handleRefresh}
                onDownload={() => {}} // No download in lane view
                downloading={false}
                onSearchTypeChange={handleSearchTypeChange}
                onSearchValueChange={handleSearchValueChange}
                onSearchInputSubmit={handleSearchInputSubmit}
                selectedSearchType={selectedSearchType}
                inputFilterVal={inputFilterVal}
                filterMetadata={config?.filter_metadata || []}
                viewMode={viewMode}
                onViewModeChange={onViewModeChange}
                viewIdFromConfig={viewIdFromConfig}
                master={master}
                isMoreFilterAdded={isMoreFilterAdded}
                handleResetFilters={handleResetFilters}
            />

            <div className={classes.lanesContainer}>
                {LANE_ORDER.map((laneType) => renderLane(laneType))}
            </div>
        </div>
    );
};

const mapStateToProps = (state: ReduxStore) => {
    const { generic, master } = state;
    const { CONSIGNMENTS } = generic;
    const { appliedFilters } = CONSIGNMENTS;

    return {
        master,
        filters: appliedFilters,
    };
};

const mapDispatchToProps = (dispatch: any) => {
    const actions = {
        setfilters: setFilters,
        applyfilters: applyFilters,
    };
    return bindActionCreators(actions, dispatch);
};

const hocConfig: HocOptions = {
    connectRouter: true,
    connectJss: {
        useJss: true,
        styleSheet: laneView1Styles,
    },
    connectRedux: {
        useRedux: true,
        mapStateToProps,
        mapDispatchToProps,
    },
};

const LaneView1ListView = GenericHoc(hocConfig)(LaneView1ListViewComponent);

export default LaneView1ListView;
